/**
 * @file AuthContext.js
 * @description Authentication context provider — manages user login, logout,
 *              and session persistence via JWT stored in localStorage.
 *              Exposes `useAuth()` hook for consuming components.
 */
import React, { createContext, useState, useContext, useEffect } from 'react';
import client from '../api/client';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initial check for session
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          client.setAuthToken(token);
          const res = await client.get('/auth/profile');
          if (res.success) {
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            client.setAuthToken(null);
          }
        } catch (err) {
          console.error("Auth check failed:", err);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          client.setAuthToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    setError(null);
    try {
      const res = await client.post('/auth/login', { email, password });
      if (res.success) {
        localStorage.setItem('token', res.token);
        localStorage.setItem('user', JSON.stringify(res.user));
        client.setAuthToken(res.token);
        setUser(res.user);
        return { success: true };
      } else {
        const msg = res.message || 'Login failed';
        setError(msg);
        return { success: false, message: msg };
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed. Please check your connection.';
      setError(msg);
      return { success: false, message: msg };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    client.setAuthToken(null);
    setUser(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};